import React, { useCallback, useContext, useState } from 'react';

import { useDateLabel } from 'components/DashboardGadgetBottomBar/DateFilterTrigger';
import { useIsDateInputDisabled } from 'components/DateInput';
import DatePickerPanel from 'components/DateInput/DatePickerPanel';
import InlineDialog from 'components/InlineDialog';
import ShowOnMouseOverUnlessTour from 'components/ShowOnMouseOverUnlessTour';
import { CARD_ACTIONS_OPEN_CLASS } from 'components/Studio/CardChrome';
import {
  isSameDateSettings,
  useCurrentDateSettings,
} from 'components/Studio/dateSettings';
import Tooltip from 'components/Tooltip';
import AnalyticsContext from 'contexts/AnalyticsContext';
import CanvasContext from 'contexts/CanvasContext';
import CardContext from 'contexts/CardContext';
import PeriodsContext from 'contexts/PeriodsContext';
import WallboardContext from 'contexts/WallboardContext';
import WorkSpaceContext from 'contexts/WorkSpaceContext';
import Button from 'kingpin/atoms/Button';

// Whether the view's dates have moved off the block's saved ones. A period
// setting the range (a bonus period, or a period-filtered section) isn't a
// change made here.
const useIsChanged = ({
  savedDateSettings,
  isBonusPeriodMode,
}: {
  savedDateSettings?: DateFiltered;
  isBonusPeriodMode?: boolean;
}): boolean => {
  const current = useCurrentDateSettings();
  const { selectedPeriod } = useContext(PeriodsContext);
  if (!savedDateSettings || isBonusPeriodMode || selectedPeriod) {
    return false;
  }
  return !isSameDateSettings({ a: current, b: savedDateSettings });
};

// A Studio block's dates for this view, as a calendar icon beside its
// filters: its date picker opens from it (it changes what the block shows,
// not what's saved). The icon takes the accent while the view's dates differ
// from the saved ones, and its hint names the range. A bonus period gadget
// picks its bonus period instead.
const GadgetDateButton = ({
  savedDateSettings,
  isBonusPeriodMode,
}: {
  // The dates the block starts from, which the view's are compared with
  savedDateSettings?: DateFiltered;
  isBonusPeriodMode?: boolean;
}) => {
  const { workSpace } = useContext(WorkSpaceContext);
  const { isWallboard } = useContext(WallboardContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { setCardOnTopI } = useContext(CanvasContext);
  const { i } = useContext(CardContext);
  const isDisabled = useIsDateInputDisabled();
  const label = useDateLabel();
  const isChanged = useIsChanged({ savedDateSettings, isBonusPeriodMode });
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const open = useCallback(() => {
    if (isDisabled) {
      return;
    }
    setIsOpen(true);
    setCardOnTopI(i);
    trackEvent('Dashboard Card - Opened Date Filter', {
      isWallboardSlide: isWallboard ? 'true' : 'false',
    });
  }, [i, isDisabled, isWallboard, setCardOnTopI, trackEvent]);
  const close = useCallback(() => {
    setIsOpen(false);
    setCardOnTopI(undefined);
  }, [setCardOnTopI]);

  // A workspace sets a bonus period gadget's period, and a driver bonus
  // workspace every gadget's dates
  if (
    workSpace &&
    (isBonusPeriodMode || workSpace.campaignType === 'driverBonus')
  ) {
    return null;
  }

  return (
    <ShowOnMouseOverUnlessTour isForcedVisible={isOpen}>
      <div
        className={isOpen ? CARD_ACTIONS_OPEN_CLASS : undefined}
        data-testid="gadget-dates"
        data-is-changed={isChanged}
        style={{ marginRight: 4 }}
        // A press here, or in its picker, isn't a click on the block (e.g. a
        // drill-down)
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <InlineDialog
          placement="bottom-end"
          isOpen={isOpen}
          onClose={close}
          content={
            <DatePickerPanel
              isBonusPeriodMode={isBonusPeriodMode}
              onClosed={close}
            />
          }
        >
          <Tooltip content={label} position="top">
            <Button
              type="Ghost"
              size="Small"
              icon="calendar"
              iconColor={isChanged ? 'var(--color-brand-accent)' : undefined}
              onClick={isOpen ? close : open}
              isDisabled={isDisabled}
              testId="gadget-dates-button"
            />
          </Tooltip>
        </InlineDialog>
      </div>
    </ShowOnMouseOverUnlessTour>
  );
};

export default GadgetDateButton;
